import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useIntl } from 'react-intl';

import { IStepComponentProps, IStepperImperativeHandleProps } from '../../../../../common';
import { ExternalState, IStepsState } from '../../appointment-registration-modal.models';
import { AppointmentCheckStep as AppointmentCheckStepView } from './appointment-check-step';
import { setStoreDetail } from '../../../../../../graphql/ecp-locator/ecp-locator.cache';
import { convertDateToString } from '../../../../../../utils/date/convert-date-to-string';
import {
  AppointmentStatusType,
  CREATE_APPOINTMENT,
  CREATE_CONTROL_VISIT,
  useActionsInProgress,
  AppointmentType,
} from '../../../../../../graphql';
import { promiseErrorCallbacks } from '../../../../../../utils/promise/set-promise-error-callbacks';
import { IEcpStore } from '../../../../../../rest/ecp-locator';
import { EcpServices } from '../../../ecp-locator';
import { getAppointmentType } from '../../utils';

export const AppointmentCheckStep = forwardRef<
IStepperImperativeHandleProps, IStepComponentProps>(({
  onGoNextSuccess,
  onGoBack,
  stepsState,
  externalState,
}, ref): JSX.Element => {
  const intl = useIntl();
  const { appointmentDate } = stepsState as IStepsState;
  const storeInfo = useReactiveVar(setStoreDetail);
  const [createAppointment] = useMutation(CREATE_APPOINTMENT);
  const [createCvAppointment] = useMutation(CREATE_CONTROL_VISIT);
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const { updateAppointments } = externalState as ExternalState;
  const ecpService = (externalState as ExternalState).ecpService;
  const onRequestFitting = (externalState as ExternalState).onRequestFitting;
  const appointmentType = useMemo((): AppointmentType => {
    return getAppointmentType(ecpService);
  }, [ecpService]);

  const handleRequestAppointment = async () => {
    try {
      addActionInProgress();

      if (ecpService === EcpServices.cv) {
        await createCvAppointment({
          variables: {
            date: convertDateToString(appointmentDate!.date!),
            storeCode: (storeInfo as IEcpStore)?.code,
            status: AppointmentStatusType.unconfirmed,
            requestedTime: 'MORNING',
            appointmentType,
          },
        });
      } else {
        await createAppointment({
          variables: {
            date: convertDateToString(appointmentDate!.date!),
            storeCode: (storeInfo as IEcpStore)?.code,
            status: AppointmentStatusType.unconfirmed,
            requestedTime: 'MORNING',
          },
        });
      }

      await updateAppointments();

      onRequestFitting?.();
      onGoNextSuccess();
    } catch {
      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }
    }
    removeActionInProgress();
  };

  const pickedDate = appointmentDate?.date.toLocaleDateString(intl.locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })!;

  const onChangeFittingDate = (externalState as ExternalState).onChangeFittingDate;

  const handleGoToPicker = () => {
    onChangeFittingDate?.();
    onGoBack();
  };

  useImperativeHandle(ref, () => ({
    goNext() {
      handleRequestAppointment();
    },
    goBack() { },
  }));

  return (
    <AppointmentCheckStepView
      onGoToPicker={handleGoToPicker}
      pickedDate={pickedDate}
      storeInfo={storeInfo! as IEcpStore}
      ecpService={ecpService}
    />
  );
});
